import React, { useState, useEffect } from 'react';
import { FaChevronDown } from 'react-icons/fa6';

/**
 * MINERVA REFACTORED - História da Federação
 * Premium Design v3.0 | Cinza Chumbo + Dourado
 * Imagens do Public | Scroll Effects
 * Last Updated: 2026-01-28
 */

interface TimelineEvent {
  year: number;
  title: string;
  description: string;
  image?: string;
}

interface Stats {
  value: string;
  label: string;
}

interface Milestone {
  period: string;
  title: string;
  items: string[];
}

const Historia: React.FC = () => {
  const [expandedMilestone, setExpandedMilestone] = useState<string | null>('2017');
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-scroll-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const timeline: TimelineEvent[] = [
    {
      year: 1953,
      title: 'Fundação - O Surgimento',
      description: 'Nasce a Federação das Escolas Profissionais da Beleza. Em um Brasil que se reinventava, visionários do setor se unem para criar respeito, valorização profissional e reconhecimento da beleza como ciência, arte e carreira legítima.',
   
    },
    {
      year: 1955,
      title: 'Desfile dos Melhores Penteados',
      description: 'Primeiro grande evento de reconhecimento profissional, marcando a valorização pública da categoria.',
      image: '/foto1.jpeg',
    },
    {
      year: 1970,
      title: 'Pioneirismo em Regulamentação',
      description: 'Criação de cartilhas de boas práticas, regulamentação profissional e certificação de cursos e escolas.',
      image: '/foto2.jpeg',
    },
    {
      year: 1990,
      title: 'Expansão Internacional',
      description: 'Firmamento de parcerias com instituições da Europa, Ásia e América do Norte. Reconhecimento de diplomas em outros países, elevando o padrão global do profissional brasileiro.',
    },
    {
      year: 2017,
      title: '"Uma Nova Era" - Reposicionamento',
      description: 'Novo nome: Federação das Escolas dos Profissionais de Cabeleireiros e Similares do Brasil – Internacional. Novo selo, nova missão e slogan "DO BRASIL PARA O MUNDO".',
      image: '/dir.jpeg',
    },
  ];

  const milestones: Milestone[] = [
    {
      period: '1950s-1970s',
      title: 'Fundação e Consolidação',
      items: [
        'Organização da área da beleza, estética e terapias complementares',
        'Combate ao estigma profissional',
        'Criação de normas de boas práticas',
        'Pioneirismo em certificação de escolas',
      ],
    },
    {
      period: '1990s-2010s',
      title: 'Reconhecimento Nacional e Internacional',
      items: [
        'Expansão para cenário internacional',
        'Parcerias na Europa, Ásia e América do Norte',
        'Diplomas reconhecidos globalmente',
        'Elevação ao padrão global',
      ],
    },
    {
      period: '2017-Hoje',
      title: 'Tecnologia, Inovação e Impacto Social',
      items: [
        'Integração de plataformas digitais e QR Code',
        'Identidade profissional digital',
        'Validação documental on-line',
        'Intercâmbios internacionais de formação',
        'Projetos sociais gratuitos',
        'Eventos de alto padrão com premiações',
      ],
    },
  ];

  const stats: Stats[] = [
    { value: '70+', label: 'Anos de História' },
    { value: '4m+', label: 'Profissionais Formados' },
    { value: '140+', label: 'Países com atuação institucional' },
    { value: '100%', label: 'Padrões Internacionais de Qualidade' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        [data-scroll-animate] {
          opacity: 0;
          animation: fadeInUp 0.7s ease-out forwards;
        }

        .scroll-visible {
          opacity: 1 !important;
        }

        [data-delay="1"] { animation-delay: 0.1s; }
        [data-delay="2"] { animation-delay: 0.2s; }
        [data-delay="3"] { animation-delay: 0.3s; }
        [data-delay="4"] { animation-delay: 0.4s; }
        [data-delay="5"] { animation-delay: 0.5s; }
        [data-delay="6"] { animation-delay: 0.6s; }
        [data-delay="7"] { animation-delay: 0.7s; }

        .slide-left {
          animation: slideInLeft 0.7s ease-out forwards !important;
        }

        .slide-right {
          animation: slideInRight 0.7s ease-out forwards !important;
        }

        .scale-in {
          animation: scaleIn 0.7s ease-out forwards !important;
        }

        .timeline-item {
          position: relative;
          padding-left: 2rem;
        }

        .timeline-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 2px;
          height: 100%;
          background: linear-gradient(180deg, #d97706 0%, transparent 100%);
        }

        .timeline-dot {
          position: absolute;
          left: -7px;
          top: 4px;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #d97706;
          border: 3px solid white;
          box-shadow: 0 0 0 3px #d97706;
        }
      `}</style>

      {/* HERO SECTION */}
      <section className="relative min-h-[70vh] flex items-center bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 text-white pt-20 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gray-600 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-4xl mx-auto">
            <h1 
              id="hero-title"
              data-scroll-animate
              className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight scroll-visible"
            >
              70 Anos de História, Ética e Excelência
            </h1>
            <p 
              id="hero-desc"
              data-scroll-animate
              data-delay="1"
              className="text-xl md:text-2xl text-amber-100 mb-8 leading-relaxed scroll-visible font-light"
            >
              Desde 1953, moldando o futuro da beleza com ética, excelência e reconhecimento global.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, idx) => (
                <div 
                  key={idx}
                  id={`stat-${idx}`}
                  data-scroll-animate
                  data-delay={String(idx + 2)}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 scroll-visible"
                >
                  <p className="text-2xl md:text-3xl font-black text-amber-300">{stat.value}</p>
                  <p className="text-xs md:text-sm text-white/80">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INTRODUÇÃO */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-white to-gray-50">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <p 
              id="intro-text"
              data-scroll-animate
              className="text-xl md:text-2xl text-gray-800 leading-relaxed font-light scroll-visible mb-8"
            >
              A <span className="font-black text-amber-600">Federação das Escolas Profissionais da Beleza, Estética e Similares do Brasil – Internacional</span> tem suas origens no contexto de reorganização social e profissional do Brasil da década de 1950.
            </p>

            <div 
              id="intro-card"
              data-scroll-animate
              data-delay="1"
              className="bg-white rounded-2xl p-8 md:p-10 border-2 border-amber-200/60 shadow-xl scroll-visible"
            >
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                A Federação surgiu da articulação de profissionais, educadores e lideranças do setor da beleza que compreenderam a necessidade de institucionalizar a formação profissional, estabelecer critérios técnicos e pedagógicos e conferir legitimidade a uma atividade até então exercida de forma pouco reconhecida.<span className="font-black"> Desde sua origem, a Federação assumiu o papel de organismo institucional voltado à organização, qualificação e reconhecimento profissional, contribuindo para a consolidação da beleza como campo técnico, educacional e profissional estruturado.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE COM IMAGENS */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <h2 
            id="timeline-title"
            data-scroll-animate
            className="text-4xl md:text-5xl font-black text-gray-900 text-center mb-4 scroll-visible"
          >
            Marcos da História
          </h2>
          <p 
            id="timeline-desc"
            data-scroll-animate
            data-delay="1"
            className="text-center text-gray-600 max-w-2xl mx-auto mb-16 scroll-visible"
          >
            Uma jornada de transformação, inovação e reconhecimento global
          </p>

          <div className="max-w-5xl mx-auto space-y-12">
            {timeline.map((event, idx) => (
              <div
                key={event.year}
                id={`timeline-${event.year}`}
                data-scroll-animate
                data-delay={String(Math.min(idx + 2, 6))}
                className={`scroll-visible grid grid-cols-1 md:grid-cols-2 gap-8 items-center ${
                  idx % 2 === 1 ? 'md:direction-row-reverse' : ''
                }`}
              >
                {/* Conteúdo */}
                <div className={idx % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="bg-white rounded-xl p-6 md:p-8 border-2 border-amber-200/40 shadow-lg">
                    <div className="mb-4">
                      <span className="inline-block px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-black rounded-full">
                        {event.year}
                      </span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-4">
                      {event.title}
                    </h3>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      {event.description}
                    </p>
                  </div>
                </div>

                {/* Imagem */}
                {event.image && (
                  <div className={idx % 2 === 1 ? 'md:order-1' : ''}>
                    <div className="rounded-xl overflow-hidden shadow-xl border-2 border-amber-200/40 h-96 md:h-full">
                      <img
  src={event.image}
  alt={event.title}
  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
/>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MILESTONES EXPANDÍVEIS */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-amber-50/30">
        <div className="container mx-auto px-6 md:px-12">
          <h2 
            id="milestones-title"
            data-scroll-animate
            className="text-4xl md:text-5xl font-black text-gray-900 text-center mb-4 scroll-visible"
          >
            Períodos Históricos
          </h2>
          <p 
            id="milestones-desc"
            data-scroll-animate
            data-delay="1"
            className="text-center text-gray-600 max-w-2xl mx-auto mb-16 scroll-visible"
          >
            Cada era marca um avanço na missão de representar e regulamentar
          </p>

          <div className="max-w-3xl mx-auto space-y-4">
            {milestones.map((milestone, idx) => (
              <div
                key={milestone.period}
                id={`milestone-${milestone.period}`}
                data-scroll-animate
                data-delay={String(Math.min(idx + 2, 6))}
                className="scroll-visible"
              >
                <button
                  onClick={() => setExpandedMilestone(expandedMilestone === milestone.period ? null : milestone.period)}
                  className="w-full bg-gradient-to-r from-gray-900 via-gray-800 to-amber-900 text-white p-6 rounded-xl flex items-center justify-between hover:opacity-90 transition-all shadow-lg border border-amber-200/20"
                >
                  <div className="text-left">
                    <p className="text-sm font-black text-amber-300 mb-1">{milestone.period}</p>
                    <h3 className="text-xl md:text-2xl font-black">{milestone.title}</h3>
                  </div>
                  <FaChevronDown
                    className={`w-6 h-6 transition-transform flex-shrink-0 ${
                      expandedMilestone === milestone.period ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {expandedMilestone === milestone.period && (
                  <div className="mt-2 bg-white rounded-xl p-6 md:p-8 shadow-lg border-2 border-amber-200/40">
                    <ul className="space-y-3">
                      {milestone.items.map((item, itemIdx) => (
                        <li 
                          key={itemIdx}
                          className="flex items-start gap-4 timeline-item"
                          style={{
                            animation: `fadeInUp 0.5s ease-out ${itemIdx * 80}ms both`,
                          }}
                        >
                          <div className="timeline-dot" />
                          <span className="text-gray-700 font-medium pt-1">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRADIÇÃO COM INOVAÇÃO */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 
              id="tradition-title"
              data-scroll-animate
              className="text-4xl md:text-5xl font-black text-gray-900 text-center mb-12 scroll-visible"
            >
              Tradição com Inovação
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div 
                id="mission-card"
                data-scroll-animate
                data-delay="1"
                className="bg-gradient-to-br from-amber-50 to-orange-50/30 rounded-2xl p-8 border-2 border-amber-200/60 shadow-lg scroll-visible"
              >
                <h3 className="text-2xl font-black text-gray-900 mb-4">Nossa Missão</h3>
                <p className="text-lg text-gray-700 leading-relaxed font-light">
                  <span className="font-black text-amber-600">"Formar, representar e regulamentar escolas e profissionais da beleza, estética e bem-estar, com ética, inovação e impacto social."</span>
                </p>
              </div>

              <div 
                id="vision-card"
                data-scroll-animate
                data-delay="2"
                className="bg-gradient-to-br from-blue-50 to-cyan-50/30 rounded-2xl p-8 border-2 border-blue-200/60 shadow-lg scroll-visible"
              >
                <h3 className="text-2xl font-black text-gray-900 mb-4">Nossa Visão</h3>
                <p className="text-lg text-gray-700 leading-relaxed font-light">
                  <span className="font-black text-blue-600">"Ser referência global em representação e desenvolvimento de profissionais de beleza, garantindo qualidade, respeito e oportunidades para todos."</span>
                </p>
              </div>
            </div>

            <div 
              id="values-card"
              data-scroll-animate
              data-delay="3"
              className="mt-8 bg-white rounded-2xl p-8 md:p-10 border-2 border-amber-200/60 shadow-xl scroll-visible"
            >
              <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">Hoje, com mais de 70 anos</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                A Federação é reconhecida como um órgão regulamentador do setor da beleza, estética e terapias complementares no Brasil, sendo respeitada por sua <span className="font-black">independência, seriedade e compromisso com a excelência.</span>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200/60">
                  <p className="font-black text-amber-600 text-sm mb-2">ÉTICA</p>
                  <p className="text-gray-700">Compromisso com valores morais</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200/60">
                  <p className="font-black text-green-600 text-sm mb-2">EXCELÊNCIA</p>
                  <p className="text-gray-700">Padrão de qualidade elevado</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200/60">
                  <p className="font-black text-blue-600 text-sm mb-2">INOVAÇÃO</p>
                  <p className="text-gray-700">Tecnologia e transformação</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EVENTOS E RECONHECIMENTO */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 text-white">
        <div className="container mx-auto px-6 md:px-12">
          <h2 
            id="events-title"
            data-scroll-animate
            className="text-4xl md:text-5xl font-black text-center mb-4 scroll-visible"
          >
            Eventos de Alto Padrão
          </h2>
          <p 
            id="events-desc"
            data-scroll-animate
            data-delay="1"
            className="text-center text-amber-100 max-w-3xl mx-auto mb-12 scroll-visible"
          >
            Premiações que reconhecem os destaques do setor
          </p>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              'Congresso Educadores de Impacto',
              'Estrelas em Alto-Mar',
              'Melhores do Ano',
              'Mestres da Beleza',
              'Premiação Artesão Técnico',
              'Gestor de Excelência',
              'Prêmio Ciência e Estética',
              'Prêmio Embaixador Honorário',
            ].map((event, idx) => (
              <div
                key={idx}
                id={`event-${idx}`}
                data-scroll-animate
                data-delay={String((idx % 6) + 2)}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-center scroll-visible"
              >
                <p className="font-black text-amber-300">{event}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="container mx-auto px-6 md:px-12">
          <div 
            id="cta-final"
            data-scroll-animate
            className="max-w-3xl mx-auto bg-white rounded-2xl p-8 md:p-12 text-center shadow-xl border-2 border-amber-200 scroll-visible"
          >
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
              Faça Parte da Nossa História
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Junte-se a mais de 4 milhões profissionais que confiam na Federação para seu desenvolvimento, reconhecimento e valorização profissional.
            </p>
            <a
              href="/filiacao"
              className="inline-block px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-black rounded-lg hover:shadow-lg transition-all"
            >
              Associe-se Agora
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Historia;